import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Badge, Card, Descriptions, Spin, message } from 'antd';
import Header from '../Header';
import Sidebar from '../Sidebar';
import FeatherIcon from "feather-icons-react";
import { visitApi } from '../../api/visit.api';

const VisitDetail = () => {
    const { patientId, visitId } = useParams();
    const [visit, setVisit] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadVisit = async () => {
            try {
                setLoading(true);
                const visitData = await visitApi.getVisitById(visitId);
                setVisit(visitData);
            } catch (error) {
                console.error('Failed to load visit:', error);
                message.error('Не успяхме да заредим данните за посещението');
            } finally {
                setLoading(false);
            }
        };

        if (visitId) {
            loadVisit();
        }
    }, [visitId]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!visit) {
        return <div>Посещението не е намерено</div>;
    }

    const getVisitTypeLabel = (visit) => {
        // Use the new normalized visit type field
        return visit.visitTypeName || "Стандартно посещение";
    };

    const getExamTypeLabel = (examType) => {
        switch (examType) {
            case 11: return "Първичен преглед";
            case 12: return "Контролен преглед";
            case 13: return "Профилактичен преглед";
            default: return `Тип ${examType}`;
        }
    };

    return (
        <>
            <Header />
            <Sidebar id="menu-item4" id1="menu-items4" activeClassName="visit-detail" />
            <div className="page-wrapper">
                <div className="content">
                    {/* Page Header */}
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/visits">Прегледи</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <i className="feather-chevron-right">
                                            <FeatherIcon icon="chevron-right" />
                                        </i>
                                    </li>
                                    {patientId && (
                                        <>
                                            <li className="breadcrumb-item">
                                                <Link to={`/patients/${patientId}/visits`}>
                                                    {visit.patientName}
                                                </Link>
                                            </li>
                                            <li className="breadcrumb-item">
                                                <i className="feather-chevron-right">
                                                    <FeatherIcon icon="chevron-right" />
                                                </i>
                                            </li>
                                        </>
                                    )}
                                    <li className="breadcrumb-item active">Посещение {visit.noAl}</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Visit Actions */}
                    <div className="row mb-3">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="row align-items-center">
                                        <div className="col">
                                            <h4 className="mb-1">
                                                Амбулаторен лист № {visit.noAl}
                                                <Badge 
                                                    color={visit.signed ? "green" : "orange"}
                                                    className="ms-2"
                                                >
                                                    {visit.signed ? "Подписан" : "В процес"}
                                                </Badge>
                                            </h4>
                                            <p className="mb-0 text-muted">
                                                {new Date(visit.visitDate).toLocaleDateString('bg-BG')} в {visit.visitTime} | 
                                                Лекар: {visit.doctorName}
                                            </p>
                                        </div>
                                        <div className="col-auto">
                                            <Link 
                                                to={`/visits/${visitId}/edit`}
                                                className="btn btn-primary me-2"
                                            >
                                                <FeatherIcon icon="edit" size={16} className="me-1" />
                                                Редактирай
                                            </Link>
                                            <Link 
                                                to={patientId ? `/patients/${patientId}/visits` : "/visits"}
                                                className="btn btn-outline-primary"
                                            >
                                                <FeatherIcon icon="arrow-left" size={16} className="me-1" />
                                                {patientId ? "Обратно към посещения" : "Обратно към списъка"}
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        {/* Visit Information */}
                        <div className="col-12">
                            <Card title="Информация за посещението" className="mb-3">
                                <Descriptions column={2} size="small">
                                    <Descriptions.Item label="№ Амбулаторен лист">{visit.noAl}</Descriptions.Item>
                                    <Descriptions.Item label="Дата и час">
                                        {new Date(visit.visitDate).toLocaleDateString('bg-BG')} в {visit.visitTime}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Тип плащане">
                                        {visit.paymentType === 1 ? "НЗОК" : visit.paymentType === 2 ? "Частно" : "Договор"}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Тип преглед">
                                        <Badge color="orange">{getExamTypeLabel(visit.examType)}</Badge>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Тип посещение">
                                        <Badge color="green">{getVisitTypeLabel(visit)}</Badge>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Лекар">{visit.doctorName}</Descriptions.Item>
                                    <Descriptions.Item label="УИН">{visit.doctorUinNumber}</Descriptions.Item>
                                </Descriptions>
                            </Card>
                        </div>

                        {/* Diagnoses */}
                        <div className="col-12">
                            <Card title="Диагнози" className="mb-3">
                                {/* Main Diagnosis */}
                                {visit.mainDiagnosisName && visit.mainDiagnosisMkbCode ? (
                                    <div className="mb-3">
                                        <strong>Основна диагноза:</strong>
                                        <div className="mt-2 d-flex align-items-center">
                                            <Badge color="red" className="me-2">{visit.mainDiagnosisMkbCode}</Badge>
                                            <span>-</span>
                                            <span className="ms-2">{visit.mainDiagnosisName}</span>
                                        </div>
                                    </div>
                                ) : null}

                                {/* Additional Diagnoses */}
                                {(() => {
                                    try {
                                        const additionalDiagnoses = visit.additionalDiagnoses ? JSON.parse(visit.additionalDiagnoses) : [];
                                        return additionalDiagnoses.length > 0 ? (
                                            <div>
                                                <strong>Допълнителни диагнози:</strong>
                                                {additionalDiagnoses.map((diagnosis, index) => (
                                                    <div key={index} className="mt-2 d-flex align-items-center">
                                                        <Badge color="blue" className="me-2">{diagnosis.mkbCode}</Badge>
                                                        <span>-</span>
                                                        <span className="ms-2">{diagnosis.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null;
                                    } catch (e) {
                                        console.error('Failed to parse additional diagnoses:', e);
                                        return null;
                                    }
                                })()}

                                {/* No diagnoses message */}
                                {!visit.mainDiagnosisName && !visit.mainDiagnosisMkbCode && (!visit.additionalDiagnoses || visit.additionalDiagnoses === '[]') && (
                                    <div className="text-muted">Няма въведени диагнози</div>
                                )}
                            </Card>
                        </div>

                        {/* Clinical Data */}
                        <div className="col-12">
                            <Card title="Клинични данни">
                                <div className="mb-4">
                                    <h6>Анамнеза:</h6>
                                    <p className="text-muted">{visit.anamnesis}</p>
                                </div>

                                <div className="mb-4">
                                    <h6>Физикален преглед:</h6>
                                    <p className="text-muted">{visit.physicalExam}</p>
                                </div>

                                {visit.examinations && (
                                    <div className="mb-4">
                                        <h6>Изследвания:</h6>
                                        <p className="text-muted">{visit.examinations}</p>
                                    </div>
                                )}
                            </Card>
                        </div>

                        {/* Therapy */}
                        <div className="col-12">
                            <Card title="Терапия">
                                {visit.nonReimbursableTherapy && (
                                    <div className="mb-4">
                                        <h6>Общи терапевтични указания:</h6>
                                        <p className="text-muted">{visit.nonReimbursableTherapy}</p>
                                    </div>
                                )}

                                {/* Reimbursable Drugs */}
                                {(() => {
                                    try {
                                        const reimbursableDrugs = visit.reimbursableDrugs ? JSON.parse(visit.reimbursableDrugs) : [];
                                        return reimbursableDrugs.length > 0 ? (
                                            <div>
                                                <h6>Рецептурни медикаменти (НЗОК):</h6>
                                                <div className="table-responsive">
                                                    <table className="table table-sm">
                                                        <thead>
                                                            <tr>
                                                                <th>Код - Наименование</th>
                                                                <th>МКБ код</th>
                                                                <th>Количество</th>
                                                                <th>Дни</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {reimbursableDrugs.map((drug, drugIndex) => (
                                                                <tr key={drugIndex}>
                                                                    <td>
                                                                        <div className="d-flex align-items-center">
                                                                            <span className="fw-bold me-2">{drug.drugCode}</span>
                                                                            <span className="text-muted">-</span>
                                                                            <span className="ms-2">{drug.drugName || 'Няма наименование'}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td><Badge color="blue">{drug.mkbCode}</Badge></td>
                                                                    <td>{drug.quantity}</td>
                                                                    <td>{drug.days}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        ) : null;
                                    } catch (e) {
                                        console.error('Failed to parse reimbursable drugs:', e);
                                        return null;
                                    }
                                })()}

                                {/* No therapy message */}
                                {!visit.nonReimbursableTherapy && (!visit.reimbursableDrugs || visit.reimbursableDrugs === '[]') && (
                                    <div className="text-muted">Няма въведена терапия</div>
                                )}
                            </Card>
                        </div>

                        {/* Additional Information */}
                        {(visit.telkForm || visit.notification || visit.epicrisis || visit.medicalNote) && (
                            <div className="col-12">
                                <Card title="Документи и бележки">
                                    {visit.telkForm && (
                                        <div className="mb-3">
                                            <Badge color="orange">ТЕЛК формуляр</Badge>
                                        </div>
                                    )}
                                    {visit.notification && (
                                        <div className="mb-3">
                                            <Badge color="blue">Уведомление</Badge>
                                        </div>
                                    )}
                                    {visit.epicrisis && (
                                        <div className="mb-3">
                                            <Badge color="green">Епикриза</Badge>
                                        </div>
                                    )}
                                    {visit.medicalNote && (
                                        <div className="mb-3">
                                            <Badge color="purple">Медицинска бележка</Badge>
                                        </div>
                                    )}
                                </Card>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default VisitDetail;